﻿import {isNullOrEmpty, translation} from "@2d-front-monorepo/core";
import {
	useInitializeMvvmContext,
	MvvmDataProvider,
	MvvmDataProviderRef,
	MvvmOptionList, MvvmOptionListRenderProps, Input
} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useState, useCallback, useMemo, SyntheticEvent} from "react";
import useTariffWithDescription from "../helpers/useTariffWithDescription";
import {DefaultParkingCategory, ParkingCategory} from "../nominalTypes/ParkingCategory";
import {TariffId} from "../nominalTypes/TariffId";
import {InstallationByHostData} from "../types/InstallationByHostData";
import {ZoneOccupation} from "../types/QueryOccupationResult";

export interface SelectTariffComponentProps {
	initialState: SelectTariffModel;
	onSubmit: (formData: SelectTariffModel) => Promise<void>;
	installation: InstallationByHostData;
	tariffIds: TariffId[];
	category?: ParkingCategory;
	contextRef?: RefObject<MvvmDataProviderRef<SelectTariffModel>>;
	children?: JSX.Element;
	occupation?: ZoneOccupation[];
	skipLabelOccupation?: boolean;
	showInputFilter?: boolean;
	render?: (props: MvvmOptionListRenderProps<TariffId>) => JSX.Element;
	noDefault?: boolean;
	onChange?: (value: TariffId | undefined) => void;
}

export interface SelectTariffModel {
	tariffId: TariffId;
}

export default function SelectTariffComponent(props: SelectTariffComponentProps): JSX.Element {
	const {
		initialState,
		onSubmit,
		installation,
		tariffIds,
		occupation,
		skipLabelOccupation,
		category,
		contextRef,
		showInputFilter,
		render,
		noDefault,
		onChange,
		children
	} = props;

	const finalEntityCategory = category ?? DefaultParkingCategory;
	const tariffs = useTariffWithDescription(installation, tariffIds, finalEntityCategory, occupation, skipLabelOccupation);
	const [filteredTariffs, setFilteredTariffs] = useState(() => tariffs);

	const {context} = useInitializeMvvmContext({
		initialState: () => {
			const preSelected = tariffs.find(x => x.value === initialState.tariffId);
			const firstDefault = tariffs.find(x => x.disabled !== true);
			initialState.tariffId = preSelected == null
				? noDefault
					? ("" as TariffId)
					: firstDefault == null
						? ("" as TariffId)
						: firstDefault.value
				: preSelected.value;
			return initialState;
		},
		contextRef: contextRef
	});

	//nothing selectable (every tariff full) must block the step, not submit an empty tariffId
	const validation = useMemo(() => ({
		required: translation(PermitTranslations.validation__generic__required, {value: translation(PermitTranslations.new_permit_page__tariff_type__label)})
	}), []);

	const filterTariffs = useCallback(function (el: SyntheticEvent<HTMLInputElement>): void {
		const value = el.currentTarget.value.trim();
		if (isNullOrEmpty(value)) {
			setFilteredTariffs(tariffs);
		} else {
			setFilteredTariffs(tariffs.filter(x => x.label.toLowerCase().includes(value.toLowerCase())));
		}

	}, [tariffs]);

	return <MvvmDataProvider<SelectTariffModel> onSubmit={onSubmit} context={context} className={"layout vertical gap"}>
		{showInputFilter && <Input type={"text"} name={""} label={translation(PermitTranslations.new_permit_page__tariff_type__search__label)}
                                   placeholder={translation(PermitTranslations.new_permit_page__tariff_type__search__hint)} onChange={filterTariffs}/>}
		<MvvmOptionList context={context} property="tariffId" items={filteredTariffs} render={render} onChange={onChange} column={"flex"} validation={validation}/>
		{children}
	</MvvmDataProvider>;
}
